import type {
  AgentAnnotationEmphasis,
  AgentAnnotationShape,
} from "@/lib/agent-annotation";
import { agentInkStroke } from "./agent-ink-stroke";
import {
  AGENT_NIB,
  HEAD_TAPERS,
  MARKER_ALPHA,
  MARKER_TAPER,
  markerNibSize,
} from "./ink-style";
import { seededRandom } from "./seeded-random";
import {
  arrowHeadStrokes,
  sketchBoxPoints,
  sketchLoopPoints,
  sketchMarkerPoints,
  sketchUnderlinePoints,
} from "./sketch-shapes";
import {
  outlineSvgPath,
  strokeOutline,
  type StrokeNib,
} from "./stroke-outline";
import { strokeLength, strokePrefix, type SketchPoint } from "./stroke-points";

type ClosedSketchShape = Exclude<AgentAnnotationShape, "path" | "polyline">;

export type SketchStroke = {
  points: SketchPoint[];
  nib: StrokeNib;
  /** Fill opacity of the stroke's ink; absent means INK_ALPHA. */
  alpha?: number;
};

/** The underline starts 3 px left of the target and runs 8 px wider, with
 * its baseline 3 px below the bottom edge. */
const UNDERLINE_LEAD_PX = 3;
const UNDERLINE_EXTRA_WIDTH_PX = 8;
const UNDERLINE_DROP_PX = 3;

const NIB_SIZES: Record<AgentAnnotationEmphasis, number> = {
  subtle: 2.4,
  normal: AGENT_NIB.size,
  strong: 4.2,
};

/** Raw hand-drawn points for a closed shape in content-box pixels. */
export function sketchShapePoints(
  shape: ClosedSketchShape,
  width: number,
  height: number,
  seed: string,
): SketchPoint[] {
  const random = seededRandom(seed);
  if (shape === "circle" || shape === "ellipse") {
    return sketchLoopPoints(width, height, random);
  }
  if (shape === "marker") return sketchMarkerPoints(width, height, random);
  if (shape === "underline") {
    return sketchUnderlinePoints(width + UNDERLINE_EXTRA_WIDTH_PX, random).map(
      (point) => ({
        x: point.x - UNDERLINE_LEAD_PX,
        y: point.y + height + UNDERLINE_DROP_PX,
      }),
    );
  }
  return sketchBoxPoints(width, height, random, {
    rounded: shape === "rounded_rectangle",
  });
}

/** Pen strokes for one agent mark in content-box pixels: the shape (or the
 * path shaft) followed by the arrow head flicks when `arrowEnd` is set. A
 * marker is a flat, see-through band whose nib follows the box height, so it
 * ignores `nibSize`. */
export function sketchMarkStrokes({
  shape,
  width,
  height,
  path,
  arrowEnd = false,
  nibSize,
  seed,
}: {
  shape: AgentAnnotationShape;
  width: number;
  height: number;
  path?: readonly SketchPoint[];
  arrowEnd?: boolean;
  nibSize: number;
  seed: string;
}): SketchStroke[] {
  const nib: StrokeNib = { size: nibSize, taper: AGENT_NIB.taper };
  if (shape === "marker") {
    return [
      {
        points: agentInkStroke(
          sketchShapePoints(shape, width, height, seed),
          seed,
          { flat: true },
        ),
        nib: { size: markerNibSize(height), taper: MARKER_TAPER },
        alpha: MARKER_ALPHA,
      },
    ];
  }
  if (shape !== "path" && shape !== "polyline") {
    return [
      {
        points: agentInkStroke(
          sketchShapePoints(shape, width, height, seed),
          seed,
        ),
        nib,
      },
    ];
  }
  if (!path || path.length < 2) return [];
  const shaft = agentInkStroke(path, seed);
  const strokes: SketchStroke[] = [{ points: shaft, nib }];
  if (arrowEnd) {
    const [landing, leaving] = arrowHeadStrokes(shaft);
    strokes.push(
      {
        points: agentInkStroke(landing, `${seed}:head:0`),
        nib: { size: nibSize, taper: HEAD_TAPERS[0] },
      },
      {
        points: agentInkStroke(leaving, `${seed}:head:1`),
        nib: { size: nibSize, taper: HEAD_TAPERS[1] },
      },
    );
  }
  return strokes;
}

/** The strokes drawn at `progress` (0–1) of their total arc length, in order. */
export function revealSketchStrokes(
  strokes: readonly SketchStroke[],
  progress: number,
): SketchStroke[] {
  const clamped = Math.max(0, Math.min(1, progress));
  if (clamped >= 1) return strokes.slice();
  const lengths = strokes.map((stroke) => strokeLength(stroke.points));
  let remaining = lengths.reduce((sum, length) => sum + length, 0) * clamped;
  const revealed: SketchStroke[] = [];
  for (let index = 0; index < strokes.length && remaining > 0; index += 1) {
    const stroke = strokes[index]!;
    const length = lengths[index]!;
    if (length <= remaining) {
      revealed.push(stroke);
      remaining -= length;
      continue;
    }
    revealed.push({
      ...stroke,
      points: strokePrefix(stroke.points, remaining / length),
    });
    remaining = 0;
  }
  return revealed;
}

/** SVG fill paths of the strokes drawn at `progress`, one per revealed
 * stroke and in stroke order, so index `i` belongs to `strokes[i]`. */
export function sketchStrokePaths(
  strokes: readonly SketchStroke[],
  progress: number,
): string[] {
  return revealSketchStrokes(strokes, progress).map((stroke) =>
    outlineSvgPath(strokeOutline(stroke.points, stroke.nib)),
  );
}

/** Where the pen tip is at `progress`: the end of the revealed ink, or the
 * first point before anything is drawn. */
export function sketchMarkTip(
  strokes: readonly SketchStroke[],
  progress: number,
): { x: number; y: number } | null {
  const revealed = revealSketchStrokes(strokes, progress);
  const points =
    revealed[revealed.length - 1]?.points ?? strokes[0]?.points ?? [];
  const tip = revealed.length > 0 ? points[points.length - 1] : points[0];
  return tip ? { x: tip.x, y: tip.y } : null;
}

/** Nib size for a mark: an explicit stroke width wins over emphasis. */
export function nibSizeFor(
  emphasis: AgentAnnotationEmphasis | undefined,
  strokeWidth: number | undefined,
): number {
  return strokeWidth ?? NIB_SIZES[emphasis ?? "normal"];
}
